import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { rxActions, RxActions } from '@rx-angular/state/actions';
import { ItineraryState } from '@vessel-itinerary/state/itinerary.state';
import { PortService } from '@vessel-reports/port/state/port.service';
import { PortState } from '@vessel-reports/port/state/port.state';
import { Bunker, InventorySupplyBody } from '@vessel-reports/port/state/port.types';
import { PopupService } from 'app/core/services/popup.service';
import { catchError, exhaustMap, filter, map, of, switchMap, tap } from 'rxjs';

interface IPortAction {
  createNewPortReport: {
    body: any;
  };
  createNewPortReportSuccess: void;
  getDetailPortReport: {
    report_id: string;
  };
  getDetailPortReportSuccess: void;
  updateDetailPortReport: {
    body: any;
  };
  updateDetailPortReportSuccess: void;
  getPreviousReport: {
    vessel_id: string;
    type?: number;
  };
  getPreviousReportSuccess: void;
  deletePortReport: {
    report_id: string;
    queryParams?: Params;
  };
  deletePortReportSuccess: void;
  updateReport: {
    report_id: string;
    body: any;
  };
  updateReportSuccess: void;
  submitPortReport: {
    report_id: string;
  };
  submitPortReportSuccess: void;
  createInventorySupply: {
    body: InventorySupplyBody;
  };
  createInventorySupplySuccess: void;
  updateInventorySupply: {
    id: string;
    body: InventorySupplyBody;
  };
  updateInventorySupplySuccess: void;
  deleteInventorySupply: {
    id: string;
  };
  deleteInventorySupplySuccess: void;
  getInventoriesSupply: {
    report_id: string;
  };
  createReportBunker: {
    body: any;
  };
  createReportBunkerSuccess: void;
  updateReportBunker: {
    report_id: string;
    body: Bunker;
  };
  updateReportBunkerSuccess: void;
  deleteReportBunker: {
    report_id: string;
  };
  deleteReportBunkerSuccess: void;
  getBunker: {
    bunker_id: string;
  };
  getAdvanceFilters: {
    key?: 'port_report_type_id' | 'voyage_number' | 'date_report' | 'port_name';
    limit?: number;
    queryParams?: Params;
  };
  getAdvanceFiltersSuccess: {
    key?: 'port_report_type_id' | 'voyage_number' | 'date_report' | 'port_name';
    types?: any[];
    voyages?: any[];
    date_report?: any;
    ports?: any[];
  };
  cleanState: any;
}

@Injectable({
  providedIn: 'root'
})
export class PortActions {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);

  actions: RxActions<IPortAction> = rxActions<IPortAction>();
  private readonly _portService: PortService = inject(PortService);
  private readonly _state: PortState = inject(PortState);
  private readonly _itineraryState: ItineraryState = inject(ItineraryState);
  private readonly _popupService: PopupService = inject(PopupService);

  constructor() {
    this._subscribeToCreateNewPortReport$();
    this._subscribeToGetDetailPortReport$();
    this._subscribeToUpdateDetailReport$();
    this._subscribeToGetPreviousReport$();
    this._subscribeToDeleteReport$();
    this._subscribeToUpdateReport$();
    this._subscribeToSubmitPortReport$();
    this._createInventorySupply$();
    this._updateInventorySupply$();
    this._deleteInventorySupply$();
    this._subscribeToGetInventoriesSupply$();
    this._subscribeToCreateReportBunker$();
    this._subscribeToDeleteReportBunker$();
    this._subscribeToUpdateReportBunker$();
    this._subscribeToGetBunker$();
    this._subscribeToGetAdvanceFilter$();
    this._subscribeToCleanState$();
  }

  private _subscribeToCreateNewPortReport$() {
    this.actions.onCreateNewPortReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.createNewReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._portService.createNewReport(payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.closeLoadingDialog();
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.port_report_created_successfully')
        });

        this._state.set({
          detailPortReport: response?.data
        });
        this.actions.createNewPortReportSuccess();
      }
    );
  }

  private _subscribeToGetDetailPortReport$() {
    this.actions.onGetDetailPortReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.getDetailPortReport>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this._portService.getDetailPortReport(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        if (response?.error) {
          return this.actions.getDetailPortReportSuccess();
        }
        this._state.set({
          detailPortReport: response?.data
        });
        this.actions.getDetailPortReportSuccess();
      }
    );
  }

  private _subscribeToUpdateDetailReport$() {
    this.actions.onUpdateDetailPortReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.updateDetailReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._portService.updateDetailReport(payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      () => {
        this._popupService.closeLoadingDialog();
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.port_detail_report_updated_successfully')
        });

        this.actions.updateDetailPortReportSuccess();
      }
    );
  }

  private _subscribeToGetPreviousReport$() {
    this.actions.onGetPreviousReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.getPreviousReport>;
        return payload$.pipe(
          switchMap((payload) =>
            this._portService.getPreviousReport(payload.vessel_id, payload.type).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        if (response?.error) {
          return this.actions.getPreviousReportSuccess();
        }
        this._state.set({
          previousReport: response?.data
        });
        this.actions.getPreviousReportSuccess();
      }
    );
  }

  private _subscribeToDeleteReport$() {
    this.actions.onDeletePortReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.deleteReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._portService.deleteReport(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      () => {
        this._popupService.closeLoadingDialog();

        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.port_report_deleted_successfully')
        });
        this.actions.deletePortReportSuccess();
      }
    );
  }

  private _subscribeToUpdateReport$() {
    this.actions.onUpdateReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.updateReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._portService.updateReport(payload.report_id, payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      () => {
        this._popupService.closeLoadingDialog();
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.port_report_updated_successfully')
        });
        this.actions.updateReportSuccess();
      }
    );
  }

  private _subscribeToSubmitPortReport$() {
    this.actions.onSubmitPortReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.submitReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._portService.submitReport(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      () => {
        this._popupService.closeLoadingDialog();
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.port_report_submitted_successfully')
        });
        this.actions.submitPortReportSuccess();
      }
    );
  }

  private _createInventorySupply$() {
    this.actions.onCreateInventorySupply(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.createInventorySupply>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._portService.createInventorySupply(payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.closeLoadingDialog();
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.inventory_supply_created_successfully')
        });
        this.actions.createInventorySupplySuccess(response?.data);
      }
    );
  }

  private _updateInventorySupply$() {
    this.actions.onUpdateInventorySupply(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.updateInventorySupply>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._portService.updateInventorySupply(payload.id, payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.closeLoadingDialog();
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.inventory_supply_updated_successfully')
        });
        this.actions.updateInventorySupplySuccess(response?.data);
      }
    );
  }

  private _deleteInventorySupply$() {
    this.actions.onDeleteInventorySupply(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.deleteInventorySupply>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._portService.deleteInventorySupply(payload.id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.closeLoadingDialog();
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.inventory_supply_deleted_successfully')
        });
        this.actions.deleteInventorySupplySuccess(response?.data);
      }
    );
  }

  private _subscribeToGetInventoriesSupply$() {
    this.actions.onGetInventoriesSupply(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.getInventoriesSupply>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this._portService.getInventoriesSupply(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        if (response?.error) {
          return;
        }
        this._itineraryState.set({
          vesselMovements: response?.data
        });
      }
    );
  }

  private _subscribeToCreateReportBunker$() {
    this.actions.onCreateReportBunker(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.createReportBunker>;
        return payload$.pipe(
          switchMap((payload) =>
            this._portService.createReportBunker(payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.report_bunker_created_successfully')
        });
        this._state.set({
          detailBunker: response?.data
        });
        this.actions.createReportBunkerSuccess(response?.data);
      }
    );
  }

  private _subscribeToUpdateReportBunker$() {
    this.actions.onUpdateReportBunker(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.updateReportBunker>;
        return payload$.pipe(
          switchMap((payload) =>
            this._portService.updateReportBunker(payload.report_id, payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.report_bunker_updated_successfully')
        });
        this._state.set({
          detailBunker: response?.data
        });
        this.actions.updateReportBunkerSuccess(response?.data);
      }
    );
  }

  private _subscribeToDeleteReportBunker$() {
    this.actions.onUpdateReportBunker(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.deleteReportBunker>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this._portService.deleteReportBunker(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.report_bunker_deleted_successfully')
        });
        this.actions.deleteReportBunkerSuccess(response?.data);
      }
    );
  }

  private _subscribeToGetBunker$() {
    this.actions.onGetBunker(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.getBunker>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this._portService.getBunker(payload.bunker_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        if (response?.error) {
          return;
        }
        this._state.set({
          detailBunker: response.data
        });
      }
    );
  }

  private _subscribeToGetAdvanceFilter$(): void {
    this.actions.onGetAdvanceFilters(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._portService.getAdvanceFilters>;

        return payload$.pipe(
          switchMap((payload) =>
            this._portService.getAdvanceFilters(payload.key, payload.limit, payload.queryParams).pipe<APIResponse, APIResponse, any>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse)),
              map((response) => ({ payload, response }))
            )
          )
        );
      },
      ({ payload, response }: any) => {
        if (payload?.key === 'port_report_type_id') {
          this._state.set({
            types: response?.data
          });
          this.actions.getAdvanceFiltersSuccess({ types: response?.data, key: payload?.key });
        } else if (payload?.key === 'voyage_number') {
          this._state.set({
            types: response?.data
          });
          this.actions.getAdvanceFiltersSuccess({ voyages: response?.data, key: payload?.key });
        } else if (payload?.key === 'port_name') {
          this._state.set({
            ports: response?.data
          });
          this.actions.getAdvanceFiltersSuccess({ ports: response?.data, key: payload?.key });
        } else {
          this._state.set({
            dateReport: response?.data
          });
          this.actions.getAdvanceFiltersSuccess({ date_report: response?.data, key: payload?.key });
        }
      }
    );
  }

  private _subscribeToCleanState$() {
    this.actions.cleanState$.subscribe(() => {
      this._state.set({
        detailBunker: null,
        inventories: []
      });
    });
  }
}
